/**
 * Formatting and ordering shared by the Library's video and multicam cards
 * and the multicam set-up.
 */

/** A video in the Library, as the API returns it plus what the page tracks. */
export interface LibraryVideo {
  id: string;
  url: string;
  name: string;
  size: number;
  duration: string;
  /** Uploaded during this visit; the card shows a small "New" chip. */
  isNew?: boolean;
  /** When this visit added it (ms), for ids that carry no time. */
  addedAt?: number;
}

const pad = (value: number) => String(value).padStart(2, '0');

/** Milliseconds as a running time: "0:42", "12:05", "1:15:12". */
export const formatRunningTime = (ms: number): string => {
  const total = Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 1000)) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
};

/**
 * A stored video duration as the Library shows it, or '' when it is unknown.
 *
 * Uploads store minutes past the hour ("75:12"), which reads as "1:15:12".
 * "0:00" is what the API keeps when the length was never measured (the demo
 * video, older records), and anything unparseable ("Infinity:NaN") is
 * treated as unknown rather than shown.
 */
export const formatVideoDuration = (duration?: string | null): string => {
  if (typeof duration !== 'string') return '';
  const parts = duration.trim().split(':');
  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) return '';
  const seconds = parts.map(Number).reduce((total, part) => total * 60 + part, 0);
  return seconds > 0 ? formatRunningTime(seconds * 1000) : '';
};

/** "2:14 · 173.6 MB": the known parts, joined. */
export const joinMeta = (parts: Array<string | null | undefined | false>): string =>
  parts.filter((part): part is string => typeof part === 'string' && part.trim() !== '').join(' · ');

/**
 * When a MongoDB ObjectId was created (its first four bytes are seconds since
 * 1970), or null for any other id. Videos carry no upload date, but their ids
 * are ObjectIds minted when the upload finished, so this orders the Library.
 */
export const objectIdTime = (id?: string | null): number | null =>
  typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id) ? parseInt(id.slice(0, 8), 16) * 1000 : null;

export type LibrarySort = 'newest' | 'oldest' | 'name' | 'largest';

export const SORT_LABELS: Record<LibrarySort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  name: 'Name (A–Z)',
  largest: 'Largest first',
};

interface SortKeys<T> {
  time: (item: T) => number | null;
  name: (item: T) => string;
  size?: (item: T) => number;
}

const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/**
 * A sorted copy of `items`. Items without a known time keep their original
 * order after the dated ones, and ties keep their original order, so nothing
 * jumps around between renders.
 */
export function sortLibraryItems<T>(items: T[], sort: LibrarySort, keys: SortKeys<T>): T[] {
  const indexed = items.map((item, index) => ({ item, index }));
  indexed.sort((a, b) => {
    let result = 0;
    if (sort === 'name') {
      result = nameCollator.compare(keys.name(a.item), keys.name(b.item));
    } else if (sort === 'largest') {
      result = (keys.size?.(b.item) ?? 0) - (keys.size?.(a.item) ?? 0);
    } else {
      const timeA = keys.time(a.item);
      const timeB = keys.time(b.item);
      if (timeA !== null && timeB !== null) result = sort === 'newest' ? timeB - timeA : timeA - timeB;
      else if (timeA !== null) result = -1;
      else if (timeB !== null) result = 1;
    }
    return result || a.index - b.index;
  });
  return indexed.map(({ item }) => item);
}
